import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import DeleteDraftButton from './DeleteDraftButton';
import PopUpConfirmDeleteDraft from './PopUpConfirmDeleteDraft';

import './styles/DraftList.css';

const POPUP_ITEM_GAP = 12;
const BASE_ROW_HEIGHT = 56;

/**
 * Renders the user's incomplete post and article drafts.
 *
 * The popup variant keeps the same data and interactions as the page list,
 * while allowing its container to provide popup-specific presentation.
 */
export default function DraftList({ variant = 'default' } = {}) {
  const { state } = useStateHandler();
  const userSession = state.user_session;
  const navigate = useNavigate();
  const [activeDraftId, setActiveDraftId] = useState(null);
  const isPopupVariant = variant === 'popup';
  const rowHeight = isPopupVariant
    ? BASE_ROW_HEIGHT + POPUP_ITEM_GAP
    : BASE_ROW_HEIGHT;

  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${userSession._id}_all_drafts`,
    `${config.apis.api.url}/posts/status/incomplete?sort=-createdAt`,
    {
      injectToken: true,
      invalidateWhen: [
        'POST_DRAFT_SAVED',
        'ARTICLE_DRAFT_SAVED',
        'SUBMIT_POST',
        `${userSession._id}_REFRESH`,
      ],
    }
  );

  function removeDraft(draftId) {
    setFetchData(previous => ({
      ...previous,
      totalDocs: previous.totalDocs - 1,
      data: previous.data.filter(draft => draft._id !== draftId),
    }));
    GalleryService.removePostDraft(draftId);
  }

  useEffect(() => {
    if (
      !loading &&
      fetchData?.data &&
      fetchData.data.length < fetchData.totalDocs
    ) {
      loadMoreData();
    }
  }, [loading, fetchData?.data?.length, fetchData?.totalDocs, loadMoreData]);

  const initialLoad = loading && (!fetchData?.data || fetchData.data.length === 0);
  if (initialLoad) {
    return (
      <div className="draft-list__container draft-list__loading-box">
        <Loader />
      </div>
    );
  }

  const drafts = fetchData?.data?.filter(draft => {
    const status = (
      draft.post_status ??
      draft.status ??
      draft.draft_status ??
      draft.post_container?.post_status ??
      draft.post_container?.draft_status ??
      ''
    ).toString().toLowerCase();

    return status === 'incomplete';
  }) ?? [];

  if (!loading && drafts.length === 0) {
    return (
      <div className="draft-list__container">
        <Text className="draft-list__empty font-size-medium" paragraph>
          You don’t have any saved drafts right now.
        </Text>
      </div>
    );
  }

  const totalLoaded = fetchData?.data?.length ?? 0;
  const sortedDrafts = [...drafts].sort(
    (first, second) => new Date(second.createdAt) - new Date(first.createdAt)
  );

  return (
    <div className="draft-list__container">
      <InfiniteScroll
        className="draft-list__virtual-list remove-scrollbar"
        onLoadMore={loadMoreData}
        shouldLoadMore={totalLoaded < (fetchData?.totalDocs || 0)}
        offsetElements={10}
        listLength={sortedDrafts.length}
        loader={<Loader />}
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <List
                    autoHeight
                    width={width || 0.1}
                    height={height * sortedDrafts.length - 0.001}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    rowCount={sortedDrafts.length}
                    rowHeight={rowHeight}
                    scrollTop={scrollTop}
                    overscanRowCount={10}
                    rowRenderer={({ index, key, style }) => {
                      const draft = sortedDrafts[index];
                      const isArticle = Boolean(draft.post_container.post_title?.trim());
                      const route = isArticle
                        ? `/profile/edit-article/${draft._id}`
                        : `/profile/edit-post/${draft._id}`;
                      const title = isArticle
                        ? draft.post_container.post_title
                        : `Post #${draft._id.slice(-6)}`;
                      const textBlocks = draft.post_texts?.length
                        ? draft.post_texts
                        : draft.post_container?.post_texts || [];
                      const firstTextBlock = textBlocks[0]?.content || '';
                      let snippet = '';

                      if (!firstTextBlock.includes('data-atomic="true"')) {
                        const rawText = firstTextBlock
                          .replace(/<br\s*\/?>/gi, '')
                          .replace(/&nbsp;/gi, '')
                          .replace(/<div><\/div>/gi, '')
                          .trim();

                        if (rawText) {
                          const temporaryElement = document.createElement('div');
                          temporaryElement.innerHTML = rawText;
                          const plainText = temporaryElement.textContent || '';
                          const maxLength = 30;
                          snippet = plainText.length > maxLength
                            ? `${plainText.slice(0, maxLength - 1)}…`
                            : plainText;
                        }
                      }

                      if (!snippet) {
                        const structure = draft.post_structure
                          ?? draft.post_container?.post_structure
                          ?? [];
                        const firstContentBlock = structure.find(
                          block => block.type !== 'post_texts'
                        );
                        const blockLabels = {
                          post_polls: 'Poll',
                          post_quizzes: 'Quiz',
                          post_pictures: 'Image',
                          post_artworks: 'Artwork',
                        };
                        snippet = blockLabels[firstContentBlock?.type] || '';
                      }

                      const rowStyle = isPopupVariant
                        ? { ...style, height: rowHeight }
                        : style;
                      const isLastRow = index === sortedDrafts.length - 1;

                      return (
                        <div
                          key={key}
                          style={rowStyle}
                          ref={infiniteScrollAssigner(index)}
                        >
                          <div className={isPopupVariant ? 'pop-up-drafts__item-wrapper' : undefined}>
                            <div
                              className="draft-list__item element-non-selectable"
                              onClick={() => navigate(route, { state: { postData: draft } })}
                            >
                              <DeleteDraftButton
                                onClick={event => {
                                  event.stopPropagation();
                                  setActiveDraftId(draft._id);
                                }}
                              />

                              {activeDraftId === draft._id && (
                                <PopUpConfirmDeleteDraft
                                  open
                                  close={() => setActiveDraftId(null)}
                                  draftName={title}
                                  onConfirm={event => {
                                    event.stopPropagation();
                                    removeDraft(draft._id);
                                  }}
                                />
                              )}

                              <Text className="draft-list__title dots-on-overflow font-size-medium">
                                {snippet || title}
                              </Text>

                              <div className="draft-list__metadata">
                                <Text className="draft-list__type font-size-small">
                                  {isArticle ? 'ARTICLE' : 'POST'}
                                </Text>
                                <ForwardArrowIcon className="draft-list__enter" />
                              </div>
                            </div>
                            {!isLastRow && isPopupVariant && (
                              <div className="pop-up-drafts__item-spacer" />
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </div>
  );
}
